import dayjs from 'dayjs';
import { PrePaymentFrequencyOption, SampleSet, Server } from '../ApiModel';
import { isByo } from './OperatingSystemHelper';

// Suspended due to lack of payment etc
export function isSystemSuspended(server: Server) {
    return server.status === 'archive' && !server.cancelled_at;
}

// suspended due to cancellation
export function isCancelledSuspended(server: Server) {
    return server.status === 'archive' && !!server.cancelled_at;
}

// The server is (or was recently) the target of a DDoS attack. Single source for the
// predicate shared by the DdosAlert component's guard and the Services list's filter.
export function hasRecentDdos(server: Server): boolean {
    return server.networks.recent_ddos;
}

export function numberOfDisks(server: Server): number {
    return server.disks.length;
}

// The 1 GB floor for the disk-decrease risk's headroom band; mirrors DecreaseDiskHeadroomFloorInMegabytes
// (Domain/Entities/GuestDiskExtensions.cs).
const DECREASE_DISK_HEADROOM_FLOOR_IN_MEGABYTES = 1024;

// Mirrors the DecreaseGuestDiskSizeRisk enum (Domain/Entities/GuestDiskExtensions.cs).
export type DecreaseGuestDiskSizeRisk = 'none' | 'wontFit' | 'close';

// Mirror of GetDecreaseDiskSizeRisk (Domain/Entities/GuestDiskExtensions.cs) — see there for the policy and rationale.
// Returns the risk of resizing the primary disk to targetStorageInGigabytes; keep the two in step (integer-MB arithmetic,
// tests pin the tiers each side).
export function decreaseDiskSizeRisk(server: Server, targetStorageInGigabytes: number): DecreaseGuestDiskSizeRisk {
    // Mirrors Guest.IsManagedFileSystem.
    if (isByo(server.image)) return 'none';

    const primaryDiskInGigabytes = server.disks.find((d) => d.primary)?.size_gigabytes;
    if (primaryDiskInGigabytes === undefined) return 'none';

    if (targetStorageInGigabytes <= 0 || targetStorageInGigabytes >= primaryDiskInGigabytes) return 'none';

    // Narrow number | null | undefined explicitly: under strict mode `null > 0` fails tsc (TS18049).
    const usedInMegabytes = server.primary_disk_used_megabytes;
    if (usedInMegabytes == null || usedInMegabytes <= 0) return 'none';

    // Math.floor matches C#'s integer division of `targetInMegabytes * 2 / 100`.
    const targetInMegabytes = targetStorageInGigabytes * 1024;
    const headroomInMegabytes = Math.max(
        DECREASE_DISK_HEADROOM_FLOOR_IN_MEGABYTES,
        Math.floor((targetInMegabytes * 2) / 100)
    );

    if (usedInMegabytes >= targetInMegabytes) return 'wontFit';
    if (usedInMegabytes >= targetInMegabytes - headroomInMegabytes) return 'close';
    return 'none';
}

// Formatted per PanelSite convention (Overview.tsx), which differs cosmetically from ClassicPanel's server-side
// FormatBytes — cross-panel byte-parity is not a goal.
function primaryDiskUsedFormatted(server: Server): string {
    return `${((server.primary_disk_used_megabytes ?? 0) / 1024).toFixed(1)} GB`;
}

// The Change Plan phrasing of decreaseDiskSizeRisk. Mirror of ShouldWarnDecreaseDiskSizeMayFail
// (Domain/Entities/GuestDiskExtensions.cs); see there for why it also guards on an OS change and on disk count.
export function decreaseDiskSizeWarning(
    server: Server,
    targetStorageInGigabytes: number | undefined,
    changingOperatingSystem: boolean
): string | undefined {
    // An OS reinstall recreates the primary disk instead of shrinking the existing filesystem, so the resize2fs shrink
    // this warning predicts cannot occur and the current usage is irrelevant.
    if (changingOperatingSystem) return undefined;

    if (numberOfDisks(server) !== 1) return undefined;

    if (targetStorageInGigabytes === undefined) return undefined;

    const used = primaryDiskUsedFormatted(server);
    switch (decreaseDiskSizeRisk(server, targetStorageInGigabytes)) {
        case 'wontFit':
            return `Your server appears to be using approximately ${used}, which will not fit on the selected ${targetStorageInGigabytes} GB disk, so this plan change will very likely fail. Your server is offline during the change either way, and a failure just leaves your current plan unchanged, so you can still try.`;
        case 'close':
            return `Your server appears to be using approximately ${used}, which is close to the capacity of the selected ${targetStorageInGigabytes} GB disk, so this plan change may fail. Your server is offline during the change either way, and a failure just leaves your current plan unchanged, so you can still try.`;
        case 'none':
            return undefined;
    }
}

// The Disks page phrasing of decreaseDiskSizeRisk, for a resize of the primary disk.
export function primaryDiskResizeWarning(server: Server, targetStorageInGigabytes: number): string | undefined {
    const used = primaryDiskUsedFormatted(server);
    switch (decreaseDiskSizeRisk(server, targetStorageInGigabytes)) {
        case 'wontFit':
            return `Your server is using about ${used} which will not fit within a ${targetStorageInGigabytes} GB disk, so the resize is likely to fail. If the resize fails, the disk will stay at its current size.`;
        case 'close':
            return `Your server is using about ${used} which is close to the capacity of a ${targetStorageInGigabytes} GB disk, so the resize may fail. If the resize fails, the disk will stay at its current size.`;
        case 'none':
            return undefined;
    }
}

/**
 * Whether the memory usage agent has stopped reporting (or never reported) for a running server old enough to have
 * installed it. Mirrors Guest.AgentUpdateIsRecent and the ClassicPanel statistics warning, including the one-hour grace
 * period after creation, but only while the server is running: a stopped server's agent cannot report, so its silence
 * is not a sign that the agent is missing. An active server may still be powered off, so a recent usage sample, which is
 * only recorded while the server runs, is what shows it is running.
 */
export function isMemoryAgentOverdue(server: Server, latestSample: SampleSet | null, now: Date): boolean {
    if (server.status !== 'active') return false;

    const current = dayjs(now);
    if (!dayjs(server.created_at).add(1, 'hour').isBefore(current)) return false;
    if (!latestSample || !dayjs(latestSample.period.start).add(15, 'minute').isAfter(current)) return false;

    const reportedAt = server.memory_agent_reported_at;
    return !reportedAt || !dayjs(reportedAt).add(15, 'minute').isAfter(current);
}

export function disksTotalSizeGB(server: Server): number {
    return server.disks.reduce((size, disk) => (size = size + disk.size_gigabytes), 0);
}

export function unallocatedStorage(server: Server): number {
    return server.disk - disksTotalSizeGB(server);
}

export function getPrePaidOption(
    server: Server,
    prePaymentFrequencyOptions: PrePaymentFrequencyOption[]
): PrePaymentFrequencyOption | undefined {
    return prePaymentFrequencyOptions.find(
        (option) => option.pre_payment_frequency === server.pre_payment?.pre_payment_frequency
    );
}

export function getPrePaidPeriodFractionRemaining(
    server: Server,
    prePaymentFrequencyOptions: PrePaymentFrequencyOption[]
): number | undefined {
    if (!server.pre_payment?.paid_until) {
        return;
    }

    const prePaymentFrequencyOption = getPrePaidOption(server, prePaymentFrequencyOptions);

    if (!prePaymentFrequencyOption) {
        return;
    }

    const paid_until = dayjs(server.pre_payment.paid_until);
    const remaining_seconds = paid_until.diff(dayjs(), 'second');
    const paid_from = paid_until.subtract(prePaymentFrequencyOption.pre_payment_period_months, 'month');
    const prepaid_seconds = paid_until.diff(paid_from, 'second');

    return remaining_seconds / prepaid_seconds;
}

export function isPostPaid(server: Server): boolean {
    return (server.pre_payment?.pre_payment_frequency ?? 'post-paid') === 'post-paid';
}

// Self-service un-cancel is only meaningful for pre-paid guests that are
// cancelled but have not yet hit lease-end (suspended). Mirrors the IsEnded
// rule in CustomerResourceService.cs.
export function canUnCancel(server: Server): boolean {
    return !!server.cancelled_at && !isPostPaid(server) && !isCancelledSuspended(server);
}
